import gql from "graphql-tag";
import mixpanel from "mixpanel-browser";
import React, { useCallback } from "react";

import { DismissNewsletterCtaMutation, SubscribeToNewsletterMutation } from "../../../../../__generated__/graphql";
import megaphone from "../../../assets/images/megaphone.svg";
import useMutationWithErrorReporting from "../../../hooks/useMutationWithErrorReporting";
import Alert from "../../design-system/Alert";
import { useToast } from "../../design-system/Toaster/context";
import styles from "../../design-system/Toaster/styles.module.scss";

const SUBSCRIBE_TO_NEWSLETTER_MUTATION = gql`
  mutation subscribeToNewsletter {
    answerNewsletterCta(subscribe: true) {
      id
      answeredNewsletterCta
    }
  }
`;

const DISMISS_NEWSLETTER_CTA_MUTATION = gql`
  mutation dismissNewsletterCta {
    answerNewsletterCta(subscribe: false) {
      id
      answeredNewsletterCta
    }
  }
`;

export default function NewsletterOptInControl() {
  const toast = useToast();

  const [subscribe, { loading: subscribeLoading }] = useMutationWithErrorReporting<SubscribeToNewsletterMutation>(
    SUBSCRIBE_TO_NEWSLETTER_MUTATION,
    { defaultMessage: "An error occurred while saving your preferences" },
  );

  const [dismiss, { loading: dismissLoading }] = useMutationWithErrorReporting<DismissNewsletterCtaMutation>(
    DISMISS_NEWSLETTER_CTA_MUTATION,
    { defaultMessage: "An error occurred while saving your preferences" },
  );

  const clickSubscribe = useCallback(async () => {
    mixpanel.track("Subscribed to newsletter");
    await subscribe();
    toast.success("Thank you for subscribing");
  }, [subscribe, toast]);

  const clickDismiss = useCallback(async () => {
    mixpanel.track("Declined to subscribe to newsletter");
    await dismiss();
  }, [dismiss]);

  return (
    <Alert
      className={styles.toastAlert}
      onClickClose={clickDismiss}
      icon={<img src={megaphone} alt="" width={96} height={88} />}
      actions={[
        {
          label: "Dismiss",
          disabled: subscribeLoading,
          variant: "ghost",
          loading: dismissLoading,
          onClick: clickDismiss,
        },
        {
          label: "Sign up",
          disabled: dismissLoading,
          variant: "primary",
          loading: subscribeLoading,
          onClick: clickSubscribe,
        },
      ]}
    >
      Sign up to our monthly newsletter to keep up to date with the latest product updates and new features
    </Alert>
  );
}
